import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Stack,
  Button,
  InputAdornment,
  CircularProgress,
  Tabs,
  Tab,
  Pagination,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Comment as CommentIcon,
  Forum as ForumIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import SEOHead from '../components/SEOHead';
import DocumentMarketingHero from '../components/DocumentMarketingHero';
import { DOCUMENT, documentPageCanvasSx, documentHeroSearchSx, documentFilterChipSx, documentSubTabsSx } from '../constants/documentTheme';
import { partnerOxbloodBtnSx } from '../components/PartnerListChrome';

const Forum = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [regions, setRegions] = useState([]);
  const [popularTags, setPopularTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [selectedRegion, setSelectedRegion] = useState(searchParams.get('region') || '');
  const [selectedCountry, setSelectedCountry] = useState(searchParams.get('country') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'newest');

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (selectedRegion) params.append('region', selectedRegion);
      if (selectedCountry) params.append('country', selectedCountry);
      params.append('sort', sortBy);
      params.append('page', pagination.page);
      params.append('limit', 20);

      const response = await api.get(`/forum/posts?${params.toString()}`);
      setPosts(response.data.posts);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  }, [search, selectedRegion, selectedCountry, sortBy, pagination.page]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/forum/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await api.get('/forum/regions');
      setRegions(response.data);
    } catch (error) {
      console.error('Error fetching regions:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await api.get('/forum/tags');
      setPopularTags(response.data.slice(0, 20));
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchRegions();
    fetchTags();
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    setSearchParams({ search, region: selectedRegion, country: selectedCountry, sort: sortBy });
    fetchPosts();
  };

  const handleTagClick = (tag) => {
    setSearch(tag);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleCountryClick = (countrySlug) => {
    setSelectedCountry(countrySlug);
    setSelectedRegion('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const forumStructuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Travel Community - Déjà Vu Nomad",
      "description": "Join our travel community to discuss trips, share experiences, and get travel advice from fellow travelers worldwide.",
      "url": "https://dejavunomad.com/forum"
    },
    {
      "@context": "https://schema.org",
      "@type": "DiscussionForumPosting",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "https://dejavunomad.com/forum"
      },
      "url": "https://dejavunomad.com/forum",
      "headline": "Déjà Vu Nomad Travel Community",
      "description": "A community forum for travelers to share experiences, ask questions, and discover travel tips for destinations around the world.",
      "text": "Join our community of travelers to share experiences, ask questions, discover travel tips, and connect with fellow adventurers from around the world.",
      "datePublished": "2024-01-01T00:00:00+00:00",
      "dateModified": new Date().toISOString(),
      "author": {
        "@type": "Organization",
        "name": "Déjà Vu Nomad",
        "url": "https://dejavunomad.com"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Déjà Vu Nomad",
        "url": "https://dejavunomad.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://dejavunomad.com/logo192.png"
        }
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://dejavunomad.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Forum",
          "item": "https://dejavunomad.com/forum"
        }
      ]
    }
  ];

  return (
    <Box sx={documentPageCanvasSx}>
      <SEOHead
        title="Travel Community | Trip Discussions & Travel Tips - Déjà Vu Nomad"
        description="Join our travel community to discuss trips, share experiences, and get travel advice. Browse discussions by region, read travel tips, and connect with fellow travelers worldwide."
        keywords="travel forum, travel community, trip discussions, travel tips, travel advice, travel experiences, travel questions, backpacker forum, vacation forum, Europe travel, Asia travel, North America travel"
        canonical="https://dejavunomad.com/forum"
        structuredData={forumStructuredData}
      />

      <DocumentMarketingHero
        eyebrow={t('nav.forum')}
        title="Travel Community"
        subtitle="Share experiences, ask questions, and connect with travelers worldwide"
      >
        <Box
          component="form"
          onSubmit={handleSearch}
          sx={{
            maxWidth: 600,
            mx: 'auto',
            display: 'flex',
            gap: 1,
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Search discussions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={documentHeroSearchSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: '1.2rem', color: DOCUMENT.oxblood }} />
                </InputAdornment>
              ),
            }}
          />
          <Button type="submit" variant="contained" sx={partnerOxbloodBtnSx}>
            Search
          </Button>
        </Box>
      </DocumentMarketingHero>

      <Container maxWidth="xl" sx={{ py: 2.5 }}>
        <Grid container spacing={3}>
          {/* Main Content */}
          <Grid item xs={12} md={8}>
            {/* Region Chips + Sort */}
            <Box sx={{ mb: 2 }}>
              {/* Region filter chips */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Chip
                  label="All"
                  onClick={() => {
                    setSelectedRegion('');
                    setSelectedCountry('');
                  }}
                  sx={documentFilterChipSx(!selectedRegion)}
                />
                {regions.map(r => (
                  <Chip
                    key={r.region}
                    label={`${r.region} (${r.post_count})`}
                    onClick={() => {
                      setSelectedRegion(r.region);
                      setSelectedCountry('');
                    }}
                    sx={documentFilterChipSx(selectedRegion === r.region)}
                  />
                ))}
              </Box>
              
              {/* Sort tabs */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {selectedRegion ? `Showing posts from ${selectedRegion}` : 'Showing all destinations'}
                  {selectedCountry && ` • ${categories.find(c => c.slug === selectedCountry)?.name || ''}`}
                </Typography>
                <Tabs
                  value={sortBy}
                  onChange={(e, val) => setSortBy(val)}
                  sx={documentSubTabsSx}
                >
                  <Tab label="Newest" value="newest" />
                  <Tab label="Popular" value="popular" />
                  <Tab label="Discussed" value="discussed" />
                </Tabs>
              </Box>
            </Box>

            {/* Posts List */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : posts.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <ForumIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No discussions found
                </Typography>
                <Typography color="text.secondary">
                  Try adjusting your search or filters
                </Typography>
              </Box>
            ) : (
              <Stack spacing={2}>
                {posts.map((post) => (
                  <Card key={post.id} sx={{ '&:hover': { boxShadow: 4 } }}>
                    <CardActionArea onClick={() => navigate(`/forum/${post.slug}`)}>
                      <CardContent>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Avatar sx={{ bgcolor: '#8B1538', width: 48, height: 48 }}>
                            {post.flag_emoji || post.category_name?.charAt(0)}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                              {post.title}
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                              <Chip
                                label={`${post.flag_emoji} ${post.category_name}`}
                                size="small"
                                sx={{ backgroundColor: '#f0f0f0' }}
                              />
                              <Chip
                                label={post.category_region}
                                size="small"
                                variant="outlined"
                              />
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {post.excerpt}
                            </Typography>
                            <Stack direction="row" spacing={2} alignItems="center">
                              <Typography variant="caption" color="text.secondary">
                                {post.author_name || post.partner_name || 'Anonymous'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(post.created_at)}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <ViewIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {post.view_count?.toLocaleString()}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CommentIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {post.comment_count}
                                </Typography>
                              </Box>
                            </Stack>
                          </Box>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
              </Stack>
            )}

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.page}
                  onChange={(e, page) => setPagination(prev => ({ ...prev, page }))}
                  color="primary"
                />
              </Box>
            )}
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            {/* New Post Button */}
            <Button
              fullWidth
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/forum/new')}
              sx={{ ...partnerOxbloodBtnSx, mb: 3 }}
            >
              Start a Discussion
            </Button>

            {/* Popular Countries */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  🌍 Popular Destinations
                </Typography>
                <Stack spacing={1}>
                  {categories
                    .filter(c => c.post_count > 0)
                    .sort((a, b) => b.post_count - a.post_count)
                    .slice(0, 10)
                    .map((category) => (
                      <Box
                        key={category.slug}
                        onClick={() => handleCountryClick(category.slug)}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          p: 1,
                          borderRadius: 1,
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#f5f5f5' },
                          backgroundColor: selectedCountry === category.slug ? '#f0f0f0' : 'transparent',
                        }}
                      >
                        <Typography>
                          {category.flag_emoji} {category.name}
                        </Typography>
                        <Chip label={category.post_count} size="small" />
                      </Box>
                    ))}
                </Stack>
              </CardContent>
            </Card>

            {/* Popular Tags */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  🏷️ Popular Topics
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {popularTags.map((item) => (
                    <Chip
                      key={item.tag}
                      label={`${item.tag} (${item.count})`}
                      size="small"
                      onClick={() => handleTagClick(item.tag)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>

            {/* Forum Stats */}
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  📊 Forum Stats
                </Typography>
                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Total Discussions</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {posts.length > 0 ? pagination.total || '1,000+' : '...'}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Regions Covered</Typography>
                    <Typography variant="body2" fontWeight={600}>{regions.length}</Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Countries</Typography>
                    <Typography variant="body2" fontWeight={600}>{categories.length}</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Forum;

